import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import { Container } from "reactstrap";
import ActionMenu from "../../../components/Common/ActionMenu";
import { get, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * AdminStaffPage — full "Staff" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 *
 * transformStaffData now also carries split firstName/lastName (not just
 * the combined `name`) so any optimistic-UI use of the row data passed
 * via navigation state has the right shape. The edit/create page itself
 * no longer depends on this state for populating the form — it always
 * fetches a fresh record by the :staffId route param — this is just a
 * defensive improvement so the row data is complete either way.
 * ---------------------------------------------------------------------- */

const AdminStaffPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Staff | Omaha Food Lovers";
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const restaurantId = location.state?.restaurantId || searchParams.get('restaurant_id');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const columns = [
    { key: "name", label: "Name", width: "1.5fr" },
    { key: "email", label: "Email", width: "1.5fr" },
    { key: "designation", label: "Designation", width: "1fr" },
    { key: "isActive", label: "Status", width: "0.8fr" },
    { key: "dateRegister", label: "Date Register", width: "1fr" },
    { key: "createdAt", label: "Created At", width: "1fr" },
    {
      key: "actions",
      label: "",
      width: "60px",
      align: "right",
      className: "rp-table__cell--actions",
      render: (row) => (
        <ActionMenu
          isOpen={activeDropdown === row.id}
          onToggle={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
          items={[
            {
              label: "Update Password",
              icon: "lock",
              onClick: () => {
                const url = restaurantId ? `/admin/staff/${row.userId}/edit?restaurant_id=${restaurantId}` : `/admin/staff/${row.userId}/edit`;
                navigate(url, { state: { staffData: row, restaurantId, updatePasswordMode: true } });
              },
            },
            {
              label: "Delete",
              icon: "delete",
              onClick: () => handleDelete(row.userId),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);

    if (restaurantId) {
      params.append("restaurant_id", restaurantId);
    }

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const transformStaffData = (staffArray) =>
    staffArray.map((staffItem) => {
      let designation = staffItem.designation_type || "N/A";
      // Capitalize first letter and replace "manager" with "Staff"
      if (designation && designation !== "N/A") {
        designation = designation.charAt(0).toUpperCase() + designation.slice(1).toLowerCase();
        designation = designation.replace(/manager/gi, "Staff");
      }
      return {
        id: staffItem.connection_id,
        userId: staffItem.user_id,
        firstName: staffItem.first_name || "",
        lastName: staffItem.last_name || "",
        name:
          `${staffItem.first_name || ""} ${staffItem.last_name || ""}`.trim() ||
          "Unknown",
        email: staffItem.email || "N/A",
        mobile: staffItem.mobile || staffItem.phone || "",
        designation: designation,
        isActive: staffItem.email_verified_at ? "Active" : "Inactive",
        dateRegister: staffItem.created_at ? new Date(staffItem.created_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : "N/A",
        createdAt: staffItem.created_at ? new Date(staffItem.created_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : "N/A",
      };
    });

  const fetchStaff = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      let endpoint;
      if (restaurantId) {
        endpoint = endpoints.adminRestaurantsStaff.replace("{{restaurant_id}}", restaurantId);
      } else {
        endpoint = endpoints.restaurantDashboardStaff;
      }
      const response = await get(`${endpoint}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const data = response.data.data;
        let newStaff;
        let currentPage, lastPage;

        if (restaurantId) {
          // New API structure for restaurant-specific staff
          newStaff = data.staff?.data || [];
          currentPage = data.staff?.current_page || 1;
          lastPage = data.staff?.last_page || 1;
        } else {
          // Existing API structure for general staff
          newStaff = data.staff || data.data || [];
          currentPage = data.current_page || 1;
          lastPage = data.last_page || 1;
        }

        const transformedStaff = transformStaffData(newStaff);

        if (isLoadMore) {
          setStaff((prev) => [...prev, ...transformedStaff]);
        } else {
          setStaff(transformedStaff);
        }

        setHasMore(currentPage < lastPage);
        setPage(currentPage);
      }
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchStaff(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleDelete = (staffId) => {
    setStaffToDelete(staffId);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const confirmDelete = async () => {
    if (!staffToDelete) return;

    try {
      let endpoint;
      if (restaurantId) {
        endpoint = endpoints.adminRestaurantsStaffDelete
          .replace("{{restaurant_id}}", restaurantId)
          .replace("{{staff_user_id}}", staffToDelete);
      } else {
        endpoint = endpoints.restaurantDashboardStaff;
        await del(`${endpoint}/${staffToDelete}`);
      }
      if (restaurantId) {
        await del(endpoint);
      }
      toastr.success("Staff member deleted successfully");
      setShowDeleteModal(false);
      setStaffToDelete(null);
      fetchStaff(1, false);
    } catch (error) {
      console.error("Failed to delete staff:", error);
      toastr.error("Failed to delete staff member");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setStaffToDelete(null);
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "created_at",
      options: [
        { value: "created_at", label: "Created At" },
        { value: "name", label: "Name" },
        { value: "email", label: "Email" },
        { value: "designation", label: "Designation" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchStaff(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Staff</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of restaurant staff members and their roles.</p>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by name or email"
            filters={filters}
          />

          <DataTable
            columns={columns}
            rows={staff}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Staff Member"
        message="Are you sure you want to delete this staff member? This action cannot be undone."
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        itemName="staff member"
      />
    </div>
  );
};

AdminStaffPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminStaffPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminStaffPage;
